import { useCallback, useEffect, useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { PaymentMethod } from '@stripe/stripe-js';
import useSWR, { KeyedMutator, useSWRConfig } from 'swr';
import { toast } from 'react-hot-toast';

import { useEvent } from '~/lib/events';
import { CreateStripePaymentMethodProps } from '~/types/hooks';
import { PaymentMethodProps } from '~/types/checkout';
import { useCheckoutAPI } from '~/hooks/checkout';
import {
  PlusSubscriptionProps,
  UpdatePaymentMethodProps,
  UsePlusActionsProps,
  AccountEmailType,
  IProductsListParams,
  IPurchasedProduct,
  IUseProductsList,
  UseProductsListData,
  CouponInfoType,
} from '~/types/account';
import {
  PAYMENT_METHODS_URL,
  COUNTRIES_REQUIRE_POSTAL_CODE,
  STEP,
} from '~/constants/checkout';
import { ACCOUNT_EMAILS_ENDPOINT, PROFILE_DATA_TYPES } from '~/constants/user';
import {
  ACCOUNT_REDEEM_GIFT_URL,
  PLUS_API_UPDATE_URL,
  PLUS_API_URL,
  PRODUCTS_API,
  ACCOUNT_UPDATE_URL,
  ACCOUNT_PM_UPDATE_URL,
  ACCOUNT_PM_DELETE_URL,
  EMAIL_UPDATE_TYPE,
  TRANSFER_API_URL,
  PRODUCTS_DOWNLOAD_API,
  PLUS_CANCEL_REASON_API_URL,
  PLUS_CANCEL_RESPONSE_API_URL,
  USER_CREDITS_URL,
} from '~/constants/accounts';
import { fetchData, fetchJson, StatusError } from '~/lib/fetch';

type PlusUpdateAction = 'cancel' | 'reactivate' | 'update_billing';
type AccountUpdateResponse = [null | string | StatusError, null | Response];
interface AddressProps {
  country: string;
  postal_code?: string;
}
const enum PM_METHOD_TYPES {
  ADD = 'add',
  REMOVE = 'remove',
  UPDATE = 'update',
}

const getPMError = (type: PM_METHOD_TYPES): string => {
  return `Cannot ${type} payment method at this time. Please try again later`;
};

/**
 * This must be called within a Stripe Element context.
 * stripe and elements hooks are initialized by the Elements context with the publishable key
 */
export function useStripePaymentHelpers() {
  const stripe = useStripe();
  const elements = useElements();
  async function createStripePaymentMethod(
    country: string,
    postalCode: string,
  ): Promise<CreateStripePaymentMethodProps> {
    const cardElement = elements?.getElement('card');

    if (!stripe || !cardElement || !cardElement) {
      return {
        error: 'Stripe is not initialized, please refresh the page.',
        paymentMethod: null,
      };
    }

    const address = {
      country,
    } as AddressProps;

    if (COUNTRIES_REQUIRE_POSTAL_CODE.includes(country)) {
      address.postal_code = postalCode;
    }

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          address,
        },
      });

      return {
        error: error?.message ?? null,
        paymentMethod: paymentMethod ?? null,
      };
    } catch (error) {
      return {
        error: 'Unable to create payment method, please try again later.',
        paymentMethod: null,
      };
    }
  }

  async function attachStripePaymentMethod(
    pm: PaymentMethod,
  ): Promise<null | string> {
    if (!pm?.id) {
      return 'Invalid payment method id. Please try again later';
    }

    const formData = new FormData();
    formData.append('payment_method_id', pm.id);

    return fetchData('/account/cards/create/', {
      method: 'POST',
      include: 'credentials',
      body: formData,
    })
      .then((response) => {
        if (response?.status === 200) {
          return null;
        }

        return getPMError(PM_METHOD_TYPES.ADD);
      })
      .catch(() => {
        return getPMError(PM_METHOD_TYPES.ADD);
      });
  }

  return { createStripePaymentMethod, attachStripePaymentMethod };
}

export function usePaymentMethods(fallbackData?: Array<PaymentMethodProps>) {
  const [isLoading, setIsLoading] = useState(false); // Ok for now to use a universal loading state
  const { data, error, mutate } = useSWR(PAYMENT_METHODS_URL, fetchJson, {
    fallbackData: fallbackData,
  });

  const sortedPaymentMethods =
    data?.sort(
      (a: PaymentMethodProps, b: PaymentMethodProps) =>
        Number(b.is_default) - Number(a.is_default),
    ) ?? [];

  async function removePaymentMethod(
    fingerprint: string,
  ): Promise<null | string> {
    if (!fingerprint) {
      return 'Missing card id. Please try again later';
    }

    const formData = new FormData();
    formData.append('fingerprint_id', fingerprint);
    setIsLoading(true);

    return fetchData(ACCOUNT_PM_DELETE_URL, {
      method: 'POST',
      body: formData,
      include: 'credentials',
    })
      .then((response) => {
        if (response?.status === 200) {
          mutate();
          return null;
        }

        return getPMError(PM_METHOD_TYPES.REMOVE);
      })
      .catch(() => {
        return getPMError(PM_METHOD_TYPES.REMOVE);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const updateDefaultPaymentMethod = async (
    card: string,
  ): Promise<null | string> => {
    if (!card) {
      return 'Missing payment method';
    }

    const formData = new FormData();
    formData.append('card', card);
    setIsLoading(true);

    return fetchData(ACCOUNT_PM_UPDATE_URL, {
      method: 'POST',
      body: formData,
      include: 'credentials',
    })
      .then((response) => {
        if (response?.status === 200) {
          mutate();
          return null;
        }

        return getPMError(PM_METHOD_TYPES.UPDATE);
      })
      .catch(() => {
        return getPMError(PM_METHOD_TYPES.UPDATE);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return {
    paymentMethods: sortedPaymentMethods,
    isLoading: isLoading || (!error && !data),
    isError: error,
    refreshPaymentMethods: mutate,
    removePaymentMethod,
    updateDefaultPaymentMethod,
  };
}

export function useAccountMethods() {
  const [isLoading, setIsLoading] = useState(false); // Ok for now to use a universal loading state

  const _formatFormData = (data: any, type: PROFILE_DATA_TYPES) => {
    const formData = new FormData();
    const country = data?.country?.value || '';
    formData.append('country', `${country}`);

    if (
      type === PROFILE_DATA_TYPES.BILLING &&
      COUNTRIES_REQUIRE_POSTAL_CODE.includes(`${country}`)
    ) {
      formData.append('postal_code', data?.postal_code);
    } else if (type === PROFILE_DATA_TYPES.TAX) {
      formData.append('tax_id', data?.tax_id ?? '');
      formData.append('tax_business_name', data?.tax_business_name ?? '');
    }

    if (type === PROFILE_DATA_TYPES.BILLING && data?.billing_name) {
      formData.append('billing_name', data?.billing_name ?? '');
    }

    return formData;
  };

  const _processResponse = (
    response: Response,
    type: PROFILE_DATA_TYPES,
  ): AccountUpdateResponse => {
    if (response?.status === 200) {
      return [null, response] as [null, Response];
    }

    // Added for typescript linting. This shouldn't happen since fetchData should throw an error if not 200
    return [
      `Cannot update ${type} details at this time. Please try again later. ${
        response?.status ?? 500
      } error.`,
      null,
    ] as [string, null];
  };

  const _deleteAccountData = async (
    type: PROFILE_DATA_TYPES,
    id: string = '',
  ): Promise<AccountUpdateResponse> => {
    const formData = new FormData();

    if (!id && type === PROFILE_DATA_TYPES.BILLING) {
      formData.append('clear_legacy_fields', 'true');
    } else if (id) {
      formData.append('entry_id', id);
    } else {
      return ['Cannot delete data at this time, missing ID.', null];
    }

    setIsLoading(true);

    return fetchData(`${ACCOUNT_UPDATE_URL}${type}/`, {
      include: 'credentials',
      method: 'PATCH',
      body: formData,
    })
      .then((response) => {
        return _processResponse(response, type);
      })
      .catch((error) => {
        return [error, null];
      })
      .finally(() => {
        setIsLoading(false);
      }) as Promise<AccountUpdateResponse>;
  };

  const _updateAccountData = async (
    data: any,
    type: PROFILE_DATA_TYPES,
  ): Promise<AccountUpdateResponse> => {
    const formData = _formatFormData(data, type);

    setIsLoading(true);

    return fetchData(`${ACCOUNT_UPDATE_URL}${type}/`, {
      include: 'credentials',
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        return _processResponse(response, type);
      })
      .catch((error) => {
        return [error, null];
      })
      .finally(() => {
        setIsLoading(false);
      }) as Promise<AccountUpdateResponse>;
  };

  async function updateBilling(data: any) {
    return _updateAccountData(data, PROFILE_DATA_TYPES.BILLING);
  }
  async function updateTax(data: any) {
    return _updateAccountData(data, PROFILE_DATA_TYPES.TAX);
  }
  async function deleteBilling(billingId?: string) {
    return _deleteAccountData(PROFILE_DATA_TYPES.BILLING, billingId);
  }
  async function deleteTax(data: any) {
    return _deleteAccountData(PROFILE_DATA_TYPES.TAX, data);
  }

  return {
    updateTax,
    updateBilling,
    deleteTax,
    deleteBilling,
    isLoading,
  };
}

export function usePlusActions(
  fallbackData?: PlusSubscriptionProps,
): UsePlusActionsProps {
  const [processing, setProcessing] = useState(false);

  const { data, error, mutate } = useSWR(PLUS_API_URL, fetchJson, {
    revalidateOnFocus: true,
    revalidateIfStale: true,
    fallbackData,
  });

  // Fetch plus cancellation reasons
  const { data: cancelReasons, error: cancelReasonsError } = useSWR(
    `${PLUS_CANCEL_REASON_API_URL}?subscription_id=${data?.id}`,
    fetchJson,
  );

  if (error || cancelReasonsError) {
    console.error('Error fetching Plus data', error || cancelReasonsError);
  }

  async function updateSubscription(
    subscriptionId: string,
    action: PlusUpdateAction,
    additionalData?: Record<string, any>,
    applyMutate: boolean = true,
  ): Promise<boolean> {
    setProcessing(true);

    try {
      const data = new FormData();

      data.append('stripe_subscription_id', subscriptionId);
      data.append('action', action);
      data.append('format', 'json');
      // add any additional data to request
      for (const key in additionalData) {
        data.append(key, additionalData[key]);
      }

      const response = await fetchJson(PLUS_API_UPDATE_URL, {
        method: 'PATCH',
        body: data,
      });

      if (!response.error) {
        applyMutate && mutate();
        toast.success(response.message || 'Subscription updated');
      } else {
        toast.error('Something went wrong');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error updating subscription', error);
      toast.error('Something went wrong');
      return false;
    } finally {
      setProcessing(false);
    }
  }

  async function cancelSubscription(
    subscriptionId?: string,
    applyMutate: boolean = true,
  ): Promise<boolean> {
    if (!subscriptionId) {
      toast.error('No active subscription found');
      return false;
    }

    return await updateSubscription(subscriptionId, 'cancel', {}, applyMutate);
  }

  async function reactivateSubscription(
    subscriptionId?: string,
  ): Promise<void> {
    if (!subscriptionId) {
      toast.error('No previous subscription found');
      return;
    }
    await updateSubscription(subscriptionId, 'reactivate');
  }

  async function updatePaymentMethod({
    subscriptionId,
    stripeToken,
  }: UpdatePaymentMethodProps): Promise<void> {
    if (!stripeToken || !subscriptionId) {
      toast.error('No payment method found');
      return;
    }

    await updateSubscription(subscriptionId, 'update_billing', {
      stripe_token: stripeToken,
    });
  }

  async function sendCancelReason({
    subscriptionId,
    reasonId,
    feedback,
  }: {
    subscriptionId: string;
    reasonId: string;
    feedback?: string;
  }): Promise<void> {
    if (!subscriptionId || !reasonId) {
      toast.error('No subscription or reason found');
      return;
    }

    const data = new FormData();

    data.append('subscription', subscriptionId);
    data.append('reason', reasonId);
    data.append('feedback', feedback ?? '');

    return await fetchJson(PLUS_CANCEL_RESPONSE_API_URL, {
      method: 'POST',
      include: 'credentials',
      body: data,
    });
  }

  return {
    cancelReasons,
    cancelSubscription,
    mutate,
    processing,
    reactivateSubscription,
    updatePaymentMethod,
    plusSubscriptionData: data,
    sendCancelReason,
  };
}

export function useEmailSettings(
  mutateAccountData: KeyedMutator<any>,
  newEmailRef?: React.RefObject<HTMLInputElement>,
  passwordRef?: React.RefObject<HTMLInputElement>,
  setNewEmailDisabled?: (disabled: boolean) => void,
  setNewEmailError?: (error: string) => void,
) {
  const [pendingAdd, setPendingAdd] = useState(false);
  const [disableResend, setDisableResend] = useState(false);
  const [disableRemove, setDisableRemove] = useState(false);
  const [disablePrimary, setDisablePrimary] = useState(false);

  const _updateEmailSettings = async (
    data: FormData,
    updateType: EMAIL_UPDATE_TYPE,
  ) => {
    if (setNewEmailDisabled) {
      setNewEmailDisabled(true);
    }

    if (updateType === EMAIL_UPDATE_TYPE.ADD) {
      setPendingAdd(true);
    } else if (updateType === EMAIL_UPDATE_TYPE.REMOVE) {
      setDisableRemove(true);
    } else if (updateType === EMAIL_UPDATE_TYPE.VERIFY) {
      setDisableResend(true);
    } else if (updateType === EMAIL_UPDATE_TYPE.PRIMARY) {
      setDisablePrimary(true);
    }

    data.append(updateType, '');

    await fetchData(ACCOUNT_EMAILS_ENDPOINT, {
      method: 'POST',
      include: 'credentials',
      body: data,
    })
      .then((response) => {
        if (response.status === 200) {
          switch (updateType) {
            case EMAIL_UPDATE_TYPE.VERIFY:
              toast.success(
                'Verification sent! Now check your email to verify this email address.',
              );
              break;
            case EMAIL_UPDATE_TYPE.ADD:
              toast.success('Email has been added.');
              if (newEmailRef?.current?.value) {
                newEmailRef.current.value = '';
              }
              if (passwordRef?.current?.value) {
                passwordRef.current.value = '';
              }
              break;
            case EMAIL_UPDATE_TYPE.REMOVE:
              toast.success('Email has been removed.');
              break;
            default:
              break;
          }
        }
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          if (setNewEmailError && updateType === EMAIL_UPDATE_TYPE.ADD) {
            setNewEmailError('Password validation failed. Please try again.');
          }

          toast.error('Password validation failed. Please try again.');
        } else {
          toast.error('Something went wrong, please try again.');
        }
      })
      .finally(() => {
        mutateAccountData();
        if (setNewEmailDisabled) {
          setNewEmailDisabled(!newEmailRef?.current?.value);
        }
        setPendingAdd(false);
        setDisableRemove(false);
        setDisableResend(false);
        setDisablePrimary(false);
      });
  };

  const onClickResendVerification = useCallback(
    (emailData: AccountEmailType) => {
      if (emailData.verified) {
        toast.error(
          'Email already verified, no need to resend verification email',
        );
        return;
      }

      const formData = new FormData();
      formData.append('email', emailData.email);

      _updateEmailSettings(formData, EMAIL_UPDATE_TYPE.VERIFY);
    },
    [_updateEmailSettings, setDisableResend],
  );

  const onClickRemove = useCallback(
    (emailData: AccountEmailType) => {
      if (emailData.primary) {
        toast.error(
          'Primary email cannot be removed. Please remove another email or add a new primary email and retry',
        );
        return;
      }

      const formData = new FormData();
      formData.append('email', emailData.email);

      _updateEmailSettings(formData, EMAIL_UPDATE_TYPE.REMOVE);
    },
    [_updateEmailSettings, setDisableRemove],
  );

  const onClickSetPrimary = useCallback(
    (emailData: AccountEmailType) => {
      if (emailData.primary) {
        toast.error('This is already your primary email.');
      }
      if (!emailData.verified) {
        toast.error('This email is not verified. Please verify it first.');
      }

      const formData = new FormData();
      formData.append('email', emailData.email);

      _updateEmailSettings(formData, EMAIL_UPDATE_TYPE.PRIMARY);
    },
    [_updateEmailSettings, setDisablePrimary],
  );

  const onSubmitModal = useCallback(
    (password: string) => {
      const newEmail = newEmailRef?.current?.value ?? '';

      if (!newEmail) {
        toast.error('Email is required, please fix and try again.');
        return;
      } else if (!password) {
        toast.error('Password is required, please fix and try again.');
        return;
      }

      const formData = new FormData();
      formData.append('email', newEmail);
      formData.append('password', password);

      _updateEmailSettings(formData, EMAIL_UPDATE_TYPE.ADD);
    },
    [newEmailRef, _updateEmailSettings, setPendingAdd],
  );

  return {
    onClickResendVerification,
    onClickRemove,
    onClickSetPrimary,
    onSubmitModal,
    pendingAdd,
    disableResend,
    disableRemove,
    disablePrimary,
  };
}

function getProductsListUrl(props: IProductsListParams) {
  const requestString = Object.keys(props)
    .filter((key: string) => !!props[key as keyof IProductsListParams])
    .map(
      (key: string) =>
        `${key}=${encodeURIComponent(
          props[key as keyof IProductsListParams] as string,
        )}`,
    )
    .join('&');

  return `${PRODUCTS_API}?${requestString}`;
}

function getProductsDownloadUrl(props: IProductsListParams) {
  const requestString = Object.keys(props)
    .filter((key: string) => !!props[key as keyof IProductsListParams])
    .map(
      (key: string) =>
        `${key}=${encodeURIComponent(
          props[key as keyof IProductsListParams] as string,
        )}`,
    )
    .join('&');

  return `${PRODUCTS_DOWNLOAD_API}?${requestString}`;
}

export function useProductsList({
  fallbackData,
}: IUseProductsList): UseProductsListData {
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [transferProcessing, setTransferProcessing] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [filter, setFilter] = useState<string | undefined>();
  const [query, setQuery] = useState<string | undefined>();

  const setParamAndResetPage =
    (paramFunc: (value: string | undefined) => void) =>
    (value: string | undefined) => {
      paramFunc(value);
      setPageIndex(1);
    };

  const { data, error, mutate, isLoading } = useSWR(
    getProductsListUrl({ page: pageIndex, f: filter, q: query }),
    fetchJson,
    {
      fallbackData: query ? [] : fallbackData,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      keepPreviousData: true,
    },
  );

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    mutate();
  }, [filter, query, isFirstLoad, mutate, pageIndex]);

  async function emailIsValid(email: string): Promise<boolean> {
    return await fetchJson(`/license/validate/email/${email.toLowerCase()}/`)
      .then(() => true)
      .catch(() => {
        toast.error('Email not associated to an AppSumo account');
        return false;
      });
  }

  async function transferProduct({
    product,
    transferEmail,
  }: {
    product: IPurchasedProduct;
    transferEmail: string;
  }): Promise<boolean> {
    setTransferProcessing(true);
    // validate email
    const validEmail = await emailIsValid(transferEmail);
    if (!validEmail) {
      setTransferProcessing(false);
      return false;
    }

    // transfer product
    const formData = new FormData();
    formData.append('invoice_item', product.id);
    formData.append('email', transferEmail.toLowerCase());
    formData.append('plan', product.plan_id);
    formData.append('action', 'transfer');

    return await fetchJson(TRANSFER_API_URL, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (response?.error) {
          toast.error(response.error);
          return false;
        } else {
          toast.success('Product transfer initiated');
          return true;
        }
      })
      .finally(() => {
        setTransferProcessing(false);
      });
  }

  return {
    products: data?.results || [],
    totalResults: isFirstLoad && isLoading ? 0 : data?.count || 0,
    error,
    mutate,
    pageIndex,
    setPageIndex,
    setFilter: setParamAndResetPage(setFilter),
    setQuery: setParamAndResetPage(setQuery),
    isLoading: isLoading || isFirstLoad,
    isEmpty: data?.results?.length === 0,
    transferProcessing,
    transferProduct,
    downloadProductsUrl: getProductsDownloadUrl({ f: filter, q: query }),
  };
}

export function useToggleCoupon(
  applyCoupon: (couponCode: string) => any,
  unapplyCoupon: (couponCode: string) => any,
) {
  // Check if setStepErrorMessage exists (is initiated in the checkout context, not in the account context)
  const { setStepErrorMessage } = useCheckoutAPI() ?? {};
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isUnapplyingCoupon, setIsUnapplyingCoupon] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Clear the existing coupon error message when a new coupon is added or removed
  useEvent('coupon:lastAdded', () => {
    errorMessage && setErrorMessage('');
  });

  const toggleCoupon = async (
    isApplyCoupon: boolean,
    coupon: CouponInfoType,
  ): Promise<boolean> => {
    let err = '';
    setErrorMessage(err);

    // If the toggle is used on checkout, remove the existing error message
    if (setStepErrorMessage) {
      setStepErrorMessage(STEP.CREDITS_COUPONS, '');
    }

    if (isApplyCoupon) {
      setIsApplyingCoupon(true);
      err = await applyCoupon(coupon.code);
    } else {
      setIsUnapplyingCoupon(true);
      err = await unapplyCoupon(coupon.code);
    }

    if (err) {
      setErrorMessage(
        `Coupon could not be ${
          isApplyCoupon ? 'applied to' : 'removed from'
        } your cart. ${err}`,
      );
    }

    setIsUnapplyingCoupon(false);
    setIsApplyingCoupon(false);

    return !!err;
  };

  return { isApplyingCoupon, isUnapplyingCoupon, errorMessage, toggleCoupon };
}

export function useGiftCardSubmission() {
  const [codeError, setCodeError] = useState('');
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const { mutate } = useSWRConfig();
  const clearCodeError = () => setCodeError('');

  const submitGiftCard = async (code: string) => {
    if (!code) return;

    setPendingSubmit(true);
    let success = false;

    try {
      const response = await fetchJson(`${ACCOUNT_REDEEM_GIFT_URL}${code}/`, {
        method: 'POST',
        include: 'credentials',
      });
      mutate(USER_CREDITS_URL);
      clearCodeError();
      toast.success(
        response?.status ?? 'Gift card has been applied to your credits.',
      );
      success = true;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setCodeError('Code could not be found');
      } else {
        setCodeError('Something went wrong.');
      }
    } finally {
      setPendingSubmit(false);
    }

    return success;
  };

  return {
    codeError,
    clearCodeError,
    pendingSubmit,
    submitGiftCard,
  };
}
